/**
 * A media query, live.
 *
 * The Calculator is two screens on a phone and one on a desktop, and which it is has to survive a
 * rotation and a resized window — a one-shot read at mount leaves an iPad turned sideways showing
 * the phone's back chevron over a desktop layout. `matchMedia` is asked once for the answer and
 * then listened to for changes.
 *
 * Guarded for the environments that have no `matchMedia`: the standalone build's tests, and any
 * server render. Both get `false`, which is the phone layout — the narrower of the two, and the
 * one that degrades into the other rather than out of it.
 */

import { useEffect, useState } from 'react';

export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);

  return matches;
}

/**
 * The keeper path's one desktop threshold, named rather than repeated.
 *
 * 900px is where the Foundations put it: cards go two-up, tables become tables, the top nav
 * replaces the floating capsule. The Calculator's split — inputs left, answer right — is the same
 * change of mind about how much room there is, so it happens at the same width.
 */
export const DESKTOP = '(min-width: 900px)';

/**
 * Where the live strips stop being part of the header and become a bar above the tab bar.
 *
 * Named here rather than repeated, because two files and one stylesheet have to agree on it: the
 * media query in `styles.css` decides whether `.dstrip` is `position: fixed`, and `dockWhen` uses
 * this to decide whether to portal it. If those two ever disagree the strip is either mispositioned
 * or portalled out of its own header — see the note in `dock.tsx`.
 */
export const STRIP_FIXED = '(max-width: 760px)';
